import { restApiDomain } from "@js/nwcc-domains.js";
import { getQueryArgs } from "@js/nwcc-navbar.js";
import { extendAddDays } from "@js/nwcc-extensions.js";
import {
    pickerToApiDateFormat,
    dateToPickerDateFormat,
    getDefaultDateConvention,
    showLoadingBar,
    networkDefaultReports,
} from "../controller/home-utils.js";

extendAddDays();

let globalMeta = [];
let globalReferenceData = {};
let globalSelectedStationMeta = {};
let globalSelectedStationData = {};
let stateOverlay = {};
let regionOverlay = {};

export const defaultNetworks = ["SNTL", "SNOW", "SCAN", "SNTLT"];
export let validNetworks = [...defaultNetworks];
export const defaultCentralTendency = "MEDIAN";
export const specialReports = {
    "YEARLY STATUS REPORT": {
        elements: ["WTEQ", "PREC", "SNWD", "TAVG"],
        duration: "daily",
        daysBack: 366,
        timeframe: "1-year",
    },
    "AIR TEMPERATURE REPORT": {
        elements: ["TAVG", "TMAX", "TMIN"],
        duration: "daily",
        daysBack: 366,
        timeframe: "1-year",
    },
    "SOIL AND CLIMATE REPORT": {
        elements: ["TOBS", "PRCP", "SMS", "SRADV", "RHUM"],
        duration: "hourly",
        daysBack: 7,
        timeframe: "7-day",
    },
    "SOIL MOISTURE REPORT": {
        elements: ["SMS", "PRCP"],
        duration: "daily",
        daysBack: 366,
        timeframe: "1-year",
    },
    "SOIL TEMPERATURE REPORT": {
        elements: ["STO", "TAVG"],
        duration: "daily",
        daysBack: 366,
        timeframe: "1-year",
    },
    "SNOW HISTORY REPORT": {
        elements: ["WTEQ", "SNWD"],
        duration: "semimonthly",
        daysBack: 3654,
        timeframe: "10-years",
    },
    "SNOW DEPTH REPORT": {
        elements: ["SNWD", "TAVG"],
        duration: "daily",
        daysBack: 366,
        timeframe: "1-year",
    },
    "WIND REPORT": {
        elements: ["WSPDV", "WSPDX", "WDIR", "WDIRV"],
        duration: "hourly",
        daysBack: 7,
        timeframe: "7-day",
    },
};

export async function addValidNetwork(network) {
    const addNetwork = network.toUpperCase();
    const awdbNetworks = getRefData().networks;
    const validNetworks = getValidNetworks();
    if (!validNetworks.includes(addNetwork) && Object.keys(awdbNetworks).includes(addNetwork)) {
        validNetworks.push(network.toUpperCase());
        await updateMeta();
    }
}

export function resetDefaultNetworks() {
    validNetworks = [...defaultNetworks];
}

export function getValidNetworks() {
    return validNetworks;
}

export function setGlobalMeta(meta) {
    globalMeta = [...meta];
}

export function setGlobalSelectedStationMeta(meta) {
    globalSelectedStationMeta = { ...meta };
}

export function setGlobalSelectedStationData(data) {
    globalSelectedStationData = { ...data };
}

export function setRefData(refData) {
    globalReferenceData = { ...refData };
}

export function setStateOverlay(geojson) {
    stateOverlay = { ...geojson };
}

export function setRegionOverlay(geojson) {
    regionOverlay = { ...geojson };
}

export function getStateOverlay() {
    return stateOverlay;
}

export function getRegionOverlay() {
    return regionOverlay;
}

export function getRefData() {
    return globalReferenceData;
}

export function getMeta() {
    return globalMeta;
}

export function getSelectedStationMeta() {
    if (document.getElementById("select--station").value === "") {
        clearGlobalSelectedStationData();
    }
    return globalSelectedStationMeta;
}

export function getAllSelectedStationElements() {
    const selectedStationElements = globalSelectedStationMeta?.stationElements
        ? globalSelectedStationMeta.stationElements
        : [];
    return selectedStationElements;
}

export function getStationDataElementsLength() {
    const length = globalSelectedStationData[0]?.data ? globalSelectedStationData[0].data.length : 0;
    return length;
}

export function getStationDataValues(idx = 0) {
    return globalSelectedStationData[0]?.data[idx]?.values || [];
}

export function getSelectedStationElement(idx = 0) {
    return globalSelectedStationData[0]?.data[idx]?.stationElement || {};
}

export async function clearGlobalSelectedStationData(dataType = "all") {
    if (dataType === "all") {
        globalSelectedStationMeta = {};
        globalSelectedStationData = {};
    }
    if (dataType === "meta") {
        globalSelectedStationMeta = {};
    }
    if (dataType === "data") {
        globalSelectedStationData = {};
    }
}

export function filterMetaDco(meta, queryArgs) {
    const dco = queryArgs?.dco;
    if (dco) {
        return meta.filter((stationMeta) => {
            return stationMeta?.dcoCode?.toUpperCase() === dco.toUpperCase();
        });
    } else {
        return meta;
    }
}

export function filterMetaBoundingBox(meta, queryArgs) {
    const boundingBox = queryArgs?.boundingBox || "";
    const boundingBoxArr = boundingBox.split(",");
    if (boundingBoxArr.length === 4) {
        const maxLat = Math.abs(boundingBoxArr[0]) || 90;
        const minLat = Math.abs(boundingBoxArr[2]) || 0;
        const maxLong = Math.abs(boundingBoxArr[1]) || 180;
        const minLong = Math.abs(boundingBoxArr[3]) || 0;
        return meta.filter((stationMeta) => {
            const lat = Math.abs(stationMeta.latitude);
            const long = Math.abs(stationMeta.longitude);
            return lat >= minLat && lat <= maxLat && long >= minLong && long <= maxLong;
        });
    } else {
        return meta;
    }
}

export async function updateMeta(queryArgs = getQueryArgs()) {
    const networks = [];
    const networkFilters = getValidNetworks();
    networkFilters.forEach((network) => {
        if (!networks.includes(network)) {
            networks.push(network);
        }
    });
    const triplets = [];
    networks.forEach(function (network) {
        triplets.push(`*:*:${network}`);
    });
    const endpoint = "stations";
    const args = `stationTriplets=${triplets.toString()}`;
    const url = `${restApiDomain}/${endpoint}?${args}`;
    // console.log(url)
    const response = await fetch(url);
    let meta = await response.json();
    meta = filterMetaBoundingBox(meta, queryArgs);
    meta = filterMetaDco(meta, queryArgs);
    setGlobalMeta(meta);
}

export function refDataToLookup(refData) {
    const refLookUp = {};
    Object.keys(refData).forEach((refDataItem) => {
        const refObj = {};
        refData[refDataItem].forEach((ref) => {
            refObj[ref.code] = ref;
        });
        refLookUp[refDataItem] = refObj;
    });
    return refLookUp;
}

export async function updateReferenceData() {
    const endpoint = "reference-data";
    const args = "referenceLists=elements,networks,units,states&visibility=all";
    const url = `${restApiDomain}/${endpoint}?${args}`;
    // console.log(url)
    const response = await fetch(url);
    const refData = await response.json();
    const refLookUp = refDataToLookup(refData);
    setRefData(refLookUp);
}

export async function updateSelectedStationMeta(queryArgs = getQueryArgs()) {
    const selectedStationTriplet = queryArgs?.stationTriplet || null;
    const stationTriplet = document.getElementById("select--station").value || selectedStationTriplet;
    const ordinal = queryArgs.ordinal || "1";
    const endpoint = "stations";
    const args = `stationTriplets=${stationTriplet}&returnStationElements=true&elements=*:*:${ordinal}`;
    const url = `${restApiDomain}/${endpoint}?${args}`;
    // console.log(url)
    const response = await fetch(url);
    const meta = await response.json();
    setGlobalSelectedStationMeta(meta[0]);
}

export function getElementTriplets(stationMeta, queryArgs) {
    const network = stationMeta?.networkCode || "Invalid Network";
    const ordinal = queryArgs?.ordinal || "1";
    const elementSelect = document.getElementById("select--element");
    let elements = elementSelect.value;
    if (elements) {
        elements = elements
            .split(",")
            .map((element) => `${element.trim()}:*:${ordinal}`)
            .join(",");
    } else {
        const specialReport = specialReports[networkDefaultReports[network.toUpperCase()]] || false;
        if (specialReport) {
            elements = specialReport.elements.map((element) => `${element.trim()}:*:${ordinal}`).join(",");
        } else {
            elements = elementSelect.options[0].value;
        }
    }
    return elements;
}

export function getSpecialReportArgs(beginDate, endDate, duration) {
    const elementSelect = document.getElementById("select--element");
    const elementLabel = elementSelect.selectedOptions[0]?.innerHTML || "Invalid Report";
    const isSpecialReport = Object.keys(specialReports).includes(elementLabel.toUpperCase());
    if (isSpecialReport) {
        duration = specialReports[elementLabel]?.duration;
        document.getElementById("select--duration").value = duration ? duration.toUpperCase() : "DAILY";
        const daysBack = specialReports[elementLabel].daysBack;
        if (daysBack) {
            beginDate = dateToPickerDateFormat(new Date().addDays(-1 * Math.abs(daysBack)));
            endDate = dateToPickerDateFormat(new Date());
        }
    }
    return [beginDate, endDate, duration];
}

export async function updateSelectedStationData(queryArgs = getQueryArgs()) {
    showLoadingBar(true);
    const selectedStationMeta = getSelectedStationMeta();
    const triplet = selectedStationMeta.stationTriplet;
    const elements = getElementTriplets(selectedStationMeta, queryArgs);
    const selectedDuration = document.getElementById("select--duration")?.value || "DAILY";
    const pickerBeginDate =
        document.getElementById("date-picker--begin")?.value || getDefaultDateConvention(selectedDuration, "begin");
    const pickerEndDate =
        document.getElementById("date-picker--end")?.value || getDefaultDateConvention(selectedDuration, "end");
    const [beginDate, endDate, duration] = getSpecialReportArgs(pickerBeginDate, pickerEndDate, selectedDuration);
    const endpoint = "data";
    const dateArgs = `beginDate=${pickerToApiDateFormat(beginDate)}&endDate=${pickerToApiDateFormat(endDate)} 23:59`;
    const ctArg = `centralTendencyType=${defaultCentralTendency}`;
    let args = `duration=${duration}&elements=${elements}&stationTriplets=${triplet}&${dateArgs}&${ctArg}`;
    if (queryArgs.returnSuspectData) {
        args += "&returnSuspectData=true";
    }
    if (queryArgs.returnFlags) {
        args += "&returnFlags=true";
    }
    if (queryArgs.returnOriginalValues) {
        args += "&returnOriginalValues=true";
    }
    if (duration.toUpperCase() === "SEMIMONTHLY") {
        args += "&periodRef=START";
    }
    const url = `${restApiDomain}/${endpoint}?${args}`;
    // console.log(url)
    const response = await fetch(url);
    const data = await response.json();
    setGlobalSelectedStationData(data);
    showLoadingBar(false);
}

export function parseApiReturnedDate(dataObj, duration) {
    if (["DAILY", "HOURLY"].includes(duration.toUpperCase())) {
        return dataObj.date;
    }
    if (duration.toUpperCase().includes("YEAR")) {
        return dataObj.year;
    }
    if (duration.toUpperCase() == "MONTHLY") {
        return `${dataObj.year}-${dataObj.month}`;
    }
    if (duration.toUpperCase() == "SEMIMONTHLY") {
        const day = dataObj.monthPart == "1" ? 1 : 16;
        return `${dataObj.year}-${dataObj.month}-${day}`;
    }
}

export async function fetchOverlays() {
    const stateUrl = "./assets/overlays/usa-states.geojson";
    const stateResponse = await fetch(stateUrl);
    setStateOverlay(await stateResponse.json());
    const regionUrl = "./assets/overlays/regions-simple.geojson";
    const regionResponse = await fetch(regionUrl);
    setRegionOverlay(await regionResponse.json());
}
